import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, BarChart3 } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import SportTabs from "../../components/terminal/SportTabs";
import {
  Cell,
  EmptyState,
  HeadCell,
  MatchupCell,
  StartTimeCell,
  fmtAmerican,
} from "../../components/terminal/DataTable";

function DisagreementBar({ bps }) {
  const v = Math.min(Math.max(Number(bps) || 0, 0), 1500);
  const pct = (v / 1500) * 100;
  const tone = v >= 500 ? "bg-red-500/70" : v >= 200 ? "bg-[#FFBF00]/70" : "bg-[#00D4FF]/50";
  return (
    <div className="w-32">
      <div className="mono text-[10px] text-zinc-400 mb-0.5">{v.toFixed(0)} bps</div>
      <div className="h-1 bg-zinc-900 border border-zinc-800">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Disagreement() {
  const [rows, setRows] = useState(null);
  const [sport, setSport] = useState("");

  const load = useCallback(async (s) => {
    try {
      const params = s ? { sport: s } : {};
      const { data } = await api.get("/terminal/disagreement", { params });
      setRows(data.rows || []);
    } catch (err) {
      console.warn("disagreement load failed", err);
      toast.error(apiErrorMessage(err, "Could not load disagreement radar"));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(sport); }, [sport, load]);

  return (
    <div data-testid="disagreement-page">
      <div className="mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <BarChart3 size={20} className="text-[#00D4FF]" />
          Market disagreement radar
        </h2>
        <p className="mono text-[11px] text-zinc-500 mt-1">
          Absolute gap between model's fair moneyline and DraftKings' live price,
          expressed in implied-probability basis points. 100 bps = 1% mispricing.
        </p>
      </div>

      <SportTabs value={sport} onChange={setSport} testidPrefix="disagreement-sport" />

      {rows === null && (
        <div className="py-8 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Scanning market…
        </div>
      )}
      {rows && rows.length === 0 && (
        <EmptyState
          testid="disagreement-empty"
          message="Model is currently aligned with the market across the evaluated slate."
        />
      )}

      {rows && rows.length > 0 && (
        <div className="border border-zinc-800 overflow-x-auto" data-testid="disagreement-table">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr>
                <HeadCell>START</HeadCell>
                <HeadCell>MATCHUP</HeadCell>
                <HeadCell className="text-right">FAIR HOME</HeadCell>
                <HeadCell className="text-right">DK HOME</HeadCell>
                <HeadCell className="text-right">Δ HOME</HeadCell>
                <HeadCell className="text-right">FAIR AWAY</HeadCell>
                <HeadCell className="text-right">DK AWAY</HeadCell>
                <HeadCell className="text-right">Δ AWAY</HeadCell>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-zinc-900/40" data-testid={`disagreement-row-${r.key}`}>
                  <Cell><StartTimeCell iso={r.start_time} /></Cell>
                  <Cell><MatchupCell row={r} /></Cell>
                  <Cell className="text-right text-[#00D4FF]">{fmtAmerican(r.fair_home)}</Cell>
                  <Cell className="text-right">{fmtAmerican(r.dk_home)}</Cell>
                  <Cell className="text-right"><DisagreementBar bps={r.disagreement_bps_home} /></Cell>
                  <Cell className="text-right text-[#00D4FF]">{fmtAmerican(r.fair_away)}</Cell>
                  <Cell className="text-right">{fmtAmerican(r.dk_away)}</Cell>
                  <Cell className="text-right"><DisagreementBar bps={r.disagreement_bps_away} /></Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
